/**
 * Admin -> Template edit form (Phase 30).
 *
 * Edits write directly to DDB (no PR flow). File upload uses a pre-signed
 * S3 PUT URL from the backend.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getAdminTemplate,
  updateAdminTemplate,
  getTemplateUploadUrl,
} from "../services/adminTemplates.js";
import { listAdminAgents } from "../services/adminAgents.js";
import { useStore } from "../store/useStore.js";
import ErrorBubble from "../components/ErrorBubble.jsx";
import LoadingSkeleton from "../components/LoadingSkeleton.jsx";

const VALID_FORMATS = ["xlsx", "pptx"];
const VALID_PARSER_TYPES = [
  "account_plan",
  "value_pyramid",
  "three_year",
  "crazy4",
];

export default function AdminTemplateEdit() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const addToast = useStore((s) => s.addToast);
  const fileRef = useRef(null);

  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tplResp, agentsResp] = await Promise.all([
        getAdminTemplate(templateId),
        listAdminAgents(),
      ]);
      const t = tplResp.template;
      setAgents(agentsResp.agents || []);
      setForm({
        name: t.name || "",
        format: t.format || "xlsx",
        s3Key: t.s3Key || "",
        applicableAgents: t.applicableAgents || [],
        parserType: t.parserType || "",
        enabled: Boolean(t.enabled),
      });
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    load();
  }, [load]);

  function toggleAgent(agentId) {
    setForm((f) => ({
      ...f,
      applicableAgents: f.applicableAgents.includes(agentId)
        ? f.applicableAgents.filter((a) => a !== agentId)
        : [...f.applicableAgents, agentId],
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await updateAdminTemplate(templateId, form);
      addToast({ type: "success", message: "Template updated." });
      navigate("/admin/templates");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleUpload() {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const { uploadUrl, s3Key } = await getTemplateUploadUrl(
        templateId,
        file.name,
      );
      const ext = file.name.split(".").pop().toLowerCase();
      const contentType =
        ext === "pptx"
          ? "application/vnd.openxmlformats-officedocument.presentationml.presentation"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const uploadResp = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": contentType },
        body: file,
      });
      if (!uploadResp.ok) {
        throw new Error(`Upload failed: ${uploadResp.status}`);
      }
      // Update template's s3Key to point to the new file
      await updateAdminTemplate(templateId, { s3Key });
      setForm((f) => ({ ...f, s3Key }));
      addToast({ type: "success", message: "Template file uploaded." });
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  if (loading) return <LoadingSkeleton variant="line" />;
  if (!form) return <ErrorBubble message={error || "Template not found"} />;

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <h1>Edit Template</h1>
        <code className="admin-muted">{templateId}</code>
      </div>

      {error && <ErrorBubble message={error} />}

      <form onSubmit={handleSubmit} className="admin-form">
        <label className="admin-form__field">
          <span>Name</span>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
        </label>

        <label className="admin-form__field">
          <span>Template ID</span>
          <input
            type="text"
            value={templateId}
            readOnly
            className="admin-form__readonly"
          />
        </label>

        <label className="admin-form__field">
          <span>Format</span>
          <select
            value={form.format}
            onChange={(e) => setForm((f) => ({ ...f, format: e.target.value }))}
          >
            {VALID_FORMATS.map((fmt) => (
              <option key={fmt} value={fmt}>
                {fmt.toUpperCase()}
              </option>
            ))}
          </select>
        </label>

        <label className="admin-form__field">
          <span>Parser Type</span>
          <select
            value={form.parserType}
            onChange={(e) =>
              setForm((f) => ({ ...f, parserType: e.target.value }))
            }
          >
            <option value="">-- select --</option>
            {VALID_PARSER_TYPES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>

        <fieldset className="admin-form__fieldset">
          <legend>Applicable Agents</legend>
          {agents.length === 0 ? (
            <p className="admin-muted">No agents available.</p>
          ) : (
            agents.map((a) => (
              <label key={a.agentId} className="admin-form__checkbox">
                <input
                  type="checkbox"
                  checked={form.applicableAgents.includes(a.agentId)}
                  onChange={() => toggleAgent(a.agentId)}
                />
                {a.name} <code>{a.agentId}</code>
              </label>
            ))
          )}
        </fieldset>

        <label className="admin-form__field">
          <span>S3 Key</span>
          <input
            type="text"
            value={form.s3Key}
            readOnly
            className="admin-form__readonly"
          />
        </label>

        <label className="admin-form__field admin-form__field--inline">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) =>
              setForm((f) => ({ ...f, enabled: e.target.checked }))
            }
          />
          <span>Enabled</span>
        </label>

        <div className="admin-form__actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? "Saving\u2026" : "Save"}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate("/admin/templates")}
          >
            Cancel
          </button>
        </div>
      </form>

      <fieldset className="admin-form__fieldset" style={{ marginTop: "2rem" }}>
        <legend>Upload Template File</legend>
        <p className="admin-page__subtext">
          Replace the template file in S3. The next pipeline run will use the
          new file.
        </p>
        <div className="admin-form__upload-row">
          <input
            type="file"
            ref={fileRef}
            accept=".xlsx,.pptx"
            disabled={uploading}
          />
          <button
            type="button"
            className="btn btn-secondary"
            disabled={uploading}
            onClick={handleUpload}
          >
            {uploading ? "Uploading\u2026" : "Upload"}
          </button>
        </div>
      </fieldset>
    </div>
  );
}
